/**
 * Domain types and helpers for customizable workspace backgrounds.
 */

import type { CSSProperties } from 'react';
import type { ThemeMode, ViewMode } from './types';

export type WorkspaceSectionId = 'radar' | 'jira' | 'slack' | 'github' | 'agents';

export type BackgroundPresetId =
  | 'default'
  | 'radar-hero'
  | 'aurora'
  | 'sunset'
  | 'grid'
  | 'dots'
  | 'slate'
  | 'none'
  | (string & {});

export interface BackgroundPreset {
  id: BackgroundPresetId;
  name: string;
  description: string;
  type?: 'mesh' | 'pattern' | 'image' | 'solid';
  imageUrl?: string;
  isCustom?: boolean;
  lightStyle: CSSProperties;
  darkStyle: CSSProperties;
}

export const WORKSPACE_SECTIONS: Array<{ id: WorkspaceSectionId; label: string }> = [
  { id: 'radar', label: 'Radar' },
  { id: 'jira', label: 'Jira' },
  { id: 'slack', label: 'Slack' },
  { id: 'github', label: 'GitHub' },
  { id: 'agents', label: 'Agentes' },
];

export const BUILTIN_BACKGROUND_PRESETS: BackgroundPreset[] = [
  {
    id: 'default',
    name: 'Mesh Suave (Predeterminado)',
    description: 'Gradiente sutil y orgánico característico de Zunelo',
    type: 'mesh',
    lightStyle: {
      backgroundColor: '#F3F5F9',
      backgroundImage: [
        'radial-gradient(at 0% 0%, rgba(199, 225, 255, 0.7) 0px, transparent 50%)',
        'radial-gradient(at 100% 0%, rgba(224, 215, 255, 0.7) 0px, transparent 50%)',
        'radial-gradient(at 100% 100%, rgba(210, 243, 235, 0.7) 0px, transparent 50%)',
        'radial-gradient(at 0% 100%, rgba(255, 228, 205, 0.6) 0px, transparent 50%)',
      ].join(', '),
    },
    darkStyle: {
      backgroundColor: '#0C111D',
      backgroundImage: [
        'radial-gradient(at 0% 0%, rgba(30, 41, 69, 0.6) 0px, transparent 50%)',
        'radial-gradient(at 100% 0%, rgba(45, 30, 75, 0.5) 0px, transparent 50%)',
        'radial-gradient(at 100% 100%, rgba(15, 45, 40, 0.5) 0px, transparent 50%)',
        'radial-gradient(at 0% 100%, rgba(45, 25, 20, 0.4) 0px, transparent 50%)',
      ].join(', '),
    },
  },
  {
    id: 'radar-hero',
    name: 'Radar 3D Hero',
    description: 'Ilustración 3D de antena parabólica y sol naciente con resplandor ambiental',
    type: 'image',
    imageUrl: '/radar.png',
    lightStyle: {
      backgroundColor: '#F3F5F9',
      backgroundImage: `url('/radar.png'), radial-gradient(at 0% 0%, rgba(199, 225, 255, 0.7) 0px, transparent 50%), radial-gradient(at 100% 100%, rgba(210, 243, 235, 0.7) 0px, transparent 50%)`,
      backgroundRepeat: 'no-repeat, no-repeat, no-repeat',
      backgroundPosition: 'center top, center center, center center',
      backgroundSize: 'cover, auto, auto',
    },
    darkStyle: {
      backgroundColor: '#0C111D',
      backgroundImage: `linear-gradient(rgba(12, 17, 29, 0.65), rgba(12, 17, 29, 0.75)), url('/radar.png'), radial-gradient(at 0% 0%, rgba(30, 41, 69, 0.6) 0px, transparent 50%)`,
      backgroundRepeat: 'no-repeat, no-repeat, no-repeat',
      backgroundPosition: 'center center, center top, center center',
      backgroundSize: 'auto, cover, auto',
    },
  },
  {
    id: 'aurora',
    name: 'Aurora Boreal',
    description: 'Emanación de tonos esmeralda, índigo y turquesa',
    type: 'mesh',
    lightStyle: {
      backgroundColor: '#F0F9FF',
      backgroundImage: [
        'radial-gradient(at 15% 15%, rgba(167, 243, 208, 0.65) 0px, transparent 55%)',
        'radial-gradient(at 85% 20%, rgba(199, 210, 254, 0.7) 0px, transparent 50%)',
        'radial-gradient(at 50% 85%, rgba(186, 230, 253, 0.7) 0px, transparent 60%)',
      ].join(', '),
    },
    darkStyle: {
      backgroundColor: '#07151D',
      backgroundImage: [
        'radial-gradient(at 15% 15%, rgba(6, 78, 59, 0.55) 0px, transparent 55%)',
        'radial-gradient(at 85% 20%, rgba(49, 46, 129, 0.6) 0px, transparent 50%)',
        'radial-gradient(at 50% 85%, rgba(12, 74, 110, 0.55) 0px, transparent 60%)',
      ].join(', '),
    },
  },
  {
    id: 'sunset',
    name: 'Atardecer Cálido',
    description: 'Tonos ámbar, melocotón y lavanda tenue',
    type: 'mesh',
    lightStyle: {
      backgroundColor: '#FFF8F5',
      backgroundImage: [
        'radial-gradient(at 0% 20%, rgba(254, 215, 170, 0.65) 0px, transparent 50%)',
        'radial-gradient(at 100% 0%, rgba(254, 202, 202, 0.6) 0px, transparent 50%)',
        'radial-gradient(at 80% 90%, rgba(233, 213, 255, 0.6) 0px, transparent 55%)',
      ].join(', '),
    },
    darkStyle: {
      backgroundColor: '#1A0E13',
      backgroundImage: [
        'radial-gradient(at 0% 20%, rgba(124, 45, 18, 0.45) 0px, transparent 50%)',
        'radial-gradient(at 100% 0%, rgba(136, 19, 55, 0.4) 0px, transparent 50%)',
        'radial-gradient(at 80% 90%, rgba(88, 28, 135, 0.45) 0px, transparent 55%)',
      ].join(', '),
    },
  },
  {
    id: 'grid',
    name: 'Cuadrícula Blueprint',
    description: 'Patrón geométrico limpio de ingeniería',
    type: 'pattern',
    lightStyle: {
      backgroundColor: '#F8FAFC',
      backgroundImage: `linear-gradient(to right, rgba(15, 23, 42, 0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(15, 23, 42, 0.05) 1px, transparent 1px)`,
      backgroundSize: '24px 24px',
    },
    darkStyle: {
      backgroundColor: '#0F172A',
      backgroundImage: `linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)`,
      backgroundSize: '24px 24px',
    },
  },
  {
    id: 'dots',
    name: 'Matriz de Puntos',
    description: 'Micropuntos discretos y minimalistas',
    type: 'pattern',
    lightStyle: {
      backgroundColor: '#F8FAFC',
      backgroundImage: `radial-gradient(rgba(15, 23, 42, 0.12) 1px, transparent 1px)`,
      backgroundSize: '18px 18px',
    },
    darkStyle: {
      backgroundColor: '#0B0F19',
      backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.12) 1px, transparent 1px)`,
      backgroundSize: '18px 18px',
    },
  },
  {
    id: 'slate',
    name: 'Slate Minimalista',
    description: 'Superficie de tono sólido con textura casi imperceptible',
    type: 'solid',
    lightStyle: {
      backgroundColor: '#EEF2F6',
      backgroundImage: 'none',
    },
    darkStyle: {
      backgroundColor: '#111827',
      backgroundImage: 'none',
    },
  },
  {
    id: 'none',
    name: 'Sin Fondo (Plano)',
    description: 'Fondo monocromático puro sin gradientes ni imágenes',
    type: 'solid',
    lightStyle: {
      backgroundColor: '#FFFFFF',
      backgroundImage: 'none',
    },
    darkStyle: {
      backgroundColor: '#0C111D',
      backgroundImage: 'none',
    },
  },
];

export const BACKGROUND_PRESETS = BUILTIN_BACKGROUND_PRESETS;

export type SectionBackgroundMap = Record<WorkspaceSectionId, BackgroundPresetId>;

export const DEFAULT_SECTION_BACKGROUNDS: SectionBackgroundMap = {
  radar: 'radar-hero',
  jira: 'default',
  slack: 'default',
  github: 'default',
  agents: 'default',
};

export const BACKGROUND_STORAGE_KEY = 'zunelo-background-config';
export const CUSTOM_BACKGROUNDS_STORAGE_KEY = 'zunelo-custom-backgrounds';

export function createCustomImagePreset(
  id: string,
  name: string,
  imageUrl: string,
  opacity: number = 0.5
): BackgroundPreset {
  return {
    id,
    name,
    description: 'Fondo de imagen personalizado',
    type: 'image',
    imageUrl,
    isCustom: true,
    lightStyle: {
      backgroundColor: '#F3F5F9',
      backgroundImage: `linear-gradient(rgba(243, 245, 249, ${1 - opacity}), rgba(243, 245, 249, ${1 - opacity})), url('${imageUrl}')`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
      backgroundSize: 'cover',
    },
    darkStyle: {
      backgroundColor: '#0C111D',
      backgroundImage: `linear-gradient(rgba(12, 17, 29, ${1 - opacity * 0.8}), rgba(12, 17, 29, ${1 - opacity * 0.8})), url('${imageUrl}')`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
      backgroundSize: 'cover',
    },
  };
}

export function getAllPresets(customPresets: BackgroundPreset[] = []): BackgroundPreset[] {
  return [...BUILTIN_BACKGROUND_PRESETS, ...customPresets];
}

export function getPresetById(
  presetId: BackgroundPresetId | string,
  customPresets: BackgroundPreset[] = []
): BackgroundPreset {
  const all = getAllPresets(customPresets);
  const found = all.find((p) => p.id === presetId);
  return found || BUILTIN_BACKGROUND_PRESETS[0];
}

export function resolveBackgroundStyle(
  view: ViewMode | string,
  sectionMap: SectionBackgroundMap,
  theme: ThemeMode,
  customPresets: BackgroundPreset[] = []
): CSSProperties {
  const sectionId = (view in sectionMap ? view : 'radar') as WorkspaceSectionId;
  const presetId = sectionMap[sectionId] || 'default';
  const preset = getPresetById(presetId, customPresets);
  return theme === 'dark' ? preset.darkStyle : preset.lightStyle;
}

export function sanitizeSectionBackgrounds(raw: unknown): SectionBackgroundMap {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_SECTION_BACKGROUNDS };
  }

  const candidate = raw as Partial<Record<string, unknown>>;
  const result: SectionBackgroundMap = { ...DEFAULT_SECTION_BACKGROUNDS };

  for (const section of WORKSPACE_SECTIONS) {
    const val = candidate[section.id];
    if (typeof val === 'string' && val.trim().length > 0) {
      result[section.id] = val as BackgroundPresetId;
    }
  }

  return result;
}

export function sanitizeCustomPresets(raw: unknown): BackgroundPreset[] {
  if (!Array.isArray(raw)) return [];
  const results: BackgroundPreset[] = [];

  for (const item of raw) {
    if (
      item &&
      typeof item === 'object' &&
      typeof item.id === 'string' &&
      typeof item.name === 'string' &&
      typeof item.imageUrl === 'string'
    ) {
      results.push(
        createCustomImagePreset(item.id, item.name, item.imageUrl, typeof item.opacity === 'number' ? item.opacity : 0.5)
      );
    }
  }

  return results;
}
